import React from 'react';
import { MessageSquare, CheckSquare, AlertOctagon, Mail, Clock, GitBranch } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ActionNodePanel = ({ onAddNode }) => {
  const actionNodes = [
    {
      type: 'sms_alert',
      icon: MessageSquare,
      label: 'SMS Alert',
      description: 'Send SMS notification via Twilio',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      defaultConfig: {
        recipientRole: 'supervisor',
        message: 'Alert notification',
        includeDetails: true
      }
    },
    {
      type: 'task_assignment',
      icon: CheckSquare,
      label: 'Task Assignment',
      description: 'Create and assign task to team member',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      defaultConfig: {
        taskTitle: 'Automated Task',
        taskDescription: '',
        assignToRole: 'field_technician',
        priority: 'high',
        dueInHours: 24
      }
    },
    {
      type: 'escalation',
      icon: AlertOctagon,
      label: 'Auto-Escalation',
      description: 'Escalate alert to management',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      defaultConfig: {
        escalateToRole: 'manager',
        notifyViaSms: true,
        notifyViaEmail: true
      }
    },
    {
      type: 'email_notification',
      icon: Mail,
      label: 'Email Notification',
      description: 'Send email notification',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      defaultConfig: {
        recipientRole: 'supervisor',
        subject: 'Workflow Notification',
        message: ''
      }
    },
    {
      type: 'delay',
      icon: Clock,
      label: 'Time Delay',
      description: 'Wait before next action',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      defaultConfig: {
        delayMinutes: 30
      }
    },
    {
      type: 'condition',
      icon: GitBranch,
      label: 'Conditional Logic',
      description: 'Branch workflow based on conditions',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      defaultConfig: {
        conditionType: 'confidence_score',
        operator: 'greater_than',
        value: 0.8
      }
    }
  ];

  const handleAddAction = (action) => {
    onAddNode({
      type: action?.type,
      label: action?.label,
      config: action?.defaultConfig,
      icon: action?.icon,
      color: action?.color,
      bgColor: action?.bgColor
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Action Library</h2>
      <p className="text-sm text-gray-600 mb-4">
        Add automated actions to your workflow
      </p>

      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {actionNodes?.map(action => {
          const IconComponent = action?.icon;
          return (
            <div
              key={action?.type}
              className="p-3 rounded-lg border border-gray-200 hover:border-green-300 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${action?.bgColor}`}>
                  <IconComponent className={`w-5 h-5 ${action?.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 text-sm">{action?.label}</h3>
                  <p className="text-xs text-gray-500 mt-1">{action?.description}</p>
                  <Button
                    onClick={() => handleAddAction(action)}
                    size="sm"
                    variant="outline"
                    className="mt-2 w-full"
                  >
                    Add Action
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-xs text-green-800">
          <strong>Tip:</strong> Chain multiple actions together to create complex automated responses. Use conditional logic to create multi-path workflows.
        </p>
      </div>
    </div>
  );
};

export default ActionNodePanel;